import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import NavShell, { Wordmark } from "@/components/NavShell";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  LogOut, BarChart3, Dumbbell, Target, CreditCard,
  Video, Users, Flame, Film, BookOpen, Home, MoreHorizontal, Swords, Backpack,
  Download, TrendingUp, Activity, ChevronDown
} from "lucide-react";
import { FormantiIcon } from "@/components/FormantiLogo";
import { useState, useEffect } from "react";
import { getSportEmoji, getSportLabel } from "@/lib/sportConfig";
import { analysesFrom, analysisWord, formatAnalyses } from "@/lib/analyses";
import { motion, AnimatePresence } from "framer-motion";

// Desktop nav items (shown in top bar). Equipment merged into Marketplace.
// Community demoted (kept reachable via Host page) until we have enough users.
const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: BarChart3 },
  { path: "/analyze", label: "Analyze", icon: Video },
  { path: "/practice", label: "Practice", icon: Activity, newUntil: "2027-01-15" },
  { path: "/progress", label: "Progress", icon: TrendingUp },
  { path: "/marketplace", label: "Gear", icon: Backpack },
  { path: "/training", label: "Training", icon: Dumbbell },
  { path: "/community?host=1", label: "Host Game", icon: Swords },
];

// A "New" tag on a nav item until this date, then it disappears by itself.
const isNewItem = (item) => !!item.newUntil && Date.now() < Date.parse(item.newUntil);

// Mobile bottom nav — 4 items + More (5 slots total, like Instagram/YouTube).
// Host was buried in the More menu, which is why nobody found it: hosting is
// how the community grows, so it earns a permanent slot and Training — a
// return-visit feature people reach from the dashboard — moves into More.
// "Gear" over "Shop": the page is AI-matched equipment recommendations, and
// "Shop" promises a storefront we don't have.
// Practice (shadow practice with the camera) is the flagship feature to market, so it
// earns a permanent slot too. Host, which took the slot above, now lives in More
// (the founder's call, 2026-10-08).
const MOBILE_NAV_PRIMARY = [
  { path: "/dashboard", label: "Home", icon: Home },
  { path: "/analyze", label: "Analyze", icon: Video },
  { path: "/practice", label: "Practice", icon: Activity, newUntil: "2027-01-15" },
  { path: "/marketplace", label: "Gear", icon: Backpack },
];

// "More" menu items on mobile
const MOBILE_NAV_MORE = [
  { path: "/community?host=1", label: "Host", icon: Swords },
  { path: "/training", label: "Training", icon: Dumbbell },
  { path: "/progress", label: "Progress", icon: BarChart3 },
  { path: "/card", label: "My Card", icon: CreditCard },
  { path: "/blog", label: "Blog", icon: BookOpen },
  { path: "/download", label: "Get App", icon: Download },
  { path: "/help", label: "Help", icon: BookOpen },
];

const SPORT_ACCENT = {
  badminton: "lime",
  table_tennis: "sky",
  swimming: "blue",
  cricket: "green",
  pickleball: "emerald",
  football: "orange",
  tennis: "amber",
};

const ACCENT_CLASSES = {
  lime: { active: "text-lime-400 bg-lime-400/10", hover: "hover:text-lime-400 hover:bg-lime-400/5", logo: "text-lime-400", border: "border-lime-400/20", dot: "bg-lime-400" },
  sky: { active: "text-sky-400 bg-sky-400/10", hover: "hover:text-sky-400 hover:bg-sky-400/5", logo: "text-sky-400", border: "border-sky-400/20", dot: "bg-sky-400" },
  blue: { active: "text-blue-400 bg-blue-400/10", hover: "hover:text-blue-400 hover:bg-blue-400/5", logo: "text-blue-400", border: "border-blue-400/20", dot: "bg-blue-400" },
  green: { active: "text-green-400 bg-green-400/10", hover: "hover:text-green-400 hover:bg-green-400/5", logo: "text-green-400", border: "border-green-400/20", dot: "bg-green-400" },
  emerald: { active: "text-emerald-400 bg-emerald-400/10", hover: "hover:text-emerald-400 hover:bg-emerald-400/5", logo: "text-emerald-400", border: "border-emerald-400/20", dot: "bg-emerald-400" },
  orange: { active: "text-orange-400 bg-orange-400/10", hover: "hover:text-orange-400 hover:bg-orange-400/5", logo: "text-orange-400", border: "border-orange-400/20", dot: "bg-orange-400" },
  amber: { active: "text-amber-400 bg-amber-400/10", hover: "hover:text-amber-400 hover:bg-amber-400/5", logo: "text-amber-400", border: "border-amber-400/20", dot: "bg-amber-400" },
};

function UserAvatar({ user, accent, size = "sm" }) {
  const initial = user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || user?.phone?.slice(-2) || "U";
  const sizeClasses = size === "sm" ? "h-8 w-8 text-xs" : "h-9 w-9 text-sm";
  return (
    <div className={`${sizeClasses} rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center font-bold text-white`}>
      {initial}
    </div>
  );
}

/** True from 1280px up (room for every link with its label); below that the extra pages go under More. */
function useWide() {
  const q = "(min-width: 1280px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(q);
    if (!mq) return undefined;
    const on = () => setWide(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return wide;
}

export default function Navbar() {
  const { isAuthenticated, user, profile, logout, tokens } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [navPulse, setNavPulse] = useState(false);
  const wide = useWide();

  // Close more menu on navigation
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // One-time bottom-nav intro: pulse the bar + auto-open the More sheet so
  // first-time visitors discover what's behind it. Mobile only.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 768) return; // desktop has top nav, skip hint
    try {
      if (localStorage.getItem("bottom_nav_intro_seen")) return;
    } catch { return; }
    // Don't fire on landing/auth/privacy — Navbar already returns null there. Nor on /practice and
    // /demo: they're pages people arrive on from a shared link, and a menu sliding up over the first
    // thing they see would be a poor welcome. The intro waits for the next page instead.
    if (["/", "/auth", "/privacy", "/demo", "/practice"].includes(location.pathname)) return;
    const pulseTimer = setTimeout(() => setNavPulse(true), 800);
    const openTimer = setTimeout(() => setMoreOpen(true), 1400);
    const closeTimer = setTimeout(() => {
      setMoreOpen(false);
      setNavPulse(false);
      try { localStorage.setItem("bottom_nav_intro_seen", "1"); } catch {}
    }, 4400);
    return () => {
      clearTimeout(pulseTimer);
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isGuest = !isAuthenticated;
  const showNav = true; // Always show navbar on app pages

  if (location.pathname === "/" || location.pathname === "/auth" || location.pathname === "/privacy" || location.pathname === "/demo") return null;

  const activeSport = profile?.active_sport || "badminton";
  const accentKey = SPORT_ACCENT[activeSport] || "lime";
  const accent = ACCENT_CLASSES[accentKey];
  // the streak is not tracked here yet: show it only when the profile carries a real, positive number (never a fake 0)
  const streak = Number(profile?.current_streak) > 0 ? Number(profile.current_streak) : 0;

  // Check if current path matches a mobile "more" item
  const isMoreActive = MOBILE_NAV_MORE.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + "/"));

  return (
    <>
      {/* ── Top Navigation Bar ── */}
      <NavShell as="nav" data-testid="navbar">
        <Wordmark to={isGuest ? "/" : "/dashboard"} testId="nav-logo" label="Formanti" />

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            {(wide ? NAV_ITEMS : NAV_ITEMS.slice(0, 3)).filter((item) => {
              // Guests don't see host (signin required) or My Card (no profile yet)
              if (isGuest && (item.path?.startsWith("/community") || item.path === "/card")) return false;
              return true;
            }).map((item) => {
              const { path, label, icon: Icon } = item;
              return (
                <Link key={path} to={path} data-testid={`nav-${label.toLowerCase()}`} aria-label={label}
                  className={`relative flex items-center gap-1.5 px-2.5 xl:px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                    location.pathname === path ? accent.active : `text-zinc-400 ${accent.hover}`
                  }`}>
                  <Icon className="w-4 h-4 lg:w-5 lg:h-5" strokeWidth={1.5} />
                  <span>{label}</span>
                  {isNewItem(item) && <span className="ml-0.5 text-[9px] font-black uppercase tracking-wide bg-lime-400 text-black rounded px-1 py-px leading-none">New</span>}
                </Link>
              );
            })}
            {wide ? (
              <Link to="/blog" data-testid="nav-blog" aria-label="Blog"
                className={`flex items-center gap-1.5 px-2.5 xl:px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname.startsWith("/blog") ? accent.active : `text-zinc-400 ${accent.hover}`
                }`}>
                <BookOpen className="w-4 h-4 lg:w-5 lg:h-5" strokeWidth={1.5} />
                <span>Blog</span>
              </Link>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" data-testid="nav-more" aria-label="More pages"
                    className={`flex items-center gap-1 px-2.5 py-2 rounded-xl text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-300/60 ${
                      NAV_ITEMS.slice(3).some((i) => location.pathname === i.path) || location.pathname.startsWith("/blog") ? accent.active : `text-zinc-400 ${accent.hover}`
                    }`}>
                    More <ChevronDown className="w-3.5 h-3.5 lg:w-5 lg:h-5" aria-hidden="true" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-zinc-900 border-zinc-800 w-48">
                  {[...NAV_ITEMS.slice(3).filter((i) => !(isGuest && i.path.startsWith("/community"))), { path: "/blog", label: "Blog", icon: BookOpen }].map(({ path, label, icon: Icon }) => (
                    <DropdownMenuItem key={path} className="text-zinc-300 focus:bg-zinc-800 cursor-pointer" onClick={() => navigate(path)} data-testid={`nav-${label.toLowerCase()}`}>
                      <Icon className="w-4 h-4 mr-2 lg:w-5 lg:h-5" strokeWidth={1.5} /> {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Right side: analyses left + streak + user menu */}
          <div className="flex items-center gap-2">
            {/* Balance chip, shown as analyses left — clickable → /wallet (visible on mobile too) */}
            {isAuthenticated && tokens != null && (
              <Link
                to="/wallet"
                title={`${formatAnalyses(tokens)} left`}
                className="flex items-center gap-1 px-2 sm:px-2.5 py-1 border border-lime-400/40 hover:bg-lime-400/10 rounded-full transition-colors" data-testid="nav-analyses"
              >
                <Video className="w-3.5 h-3.5 text-lime-300 lg:w-5 lg:h-5" />
                <span className="text-xs font-bold text-lime-300">
                  {analysesFrom(tokens).toLocaleString("en-IN")}
                  <span className="hidden sm:inline font-medium"> {analysisWord(analysesFrom(tokens))} left</span>
                </span>
              </Link>
            )}
            {/* Streak badge */}
            {isAuthenticated && profile && streak > 0 && (
              <div className="hidden sm:flex items-center gap-1 px-2.5 py-1 bg-amber-400/10 rounded-full">
                <Flame className="w-3.5 h-3.5 text-amber-400 lg:w-5 lg:h-5" />
                <span className="text-xs font-bold text-amber-400">{streak}</span>
              </div>
            )}

            {/* Sign Up button for guests — signing up grants 2 free analyses */}
            {isGuest && (
              <Button size="sm" onClick={() => {
                localStorage.removeItem("guest_mode");
                navigate("/auth");
              }}
                className="bg-lime-400 text-black hover:bg-lime-500 font-bold rounded-full text-xs sm:text-sm"
                data-testid="nav-signin-btn">
                Sign Up
              </Button>
            )}

            {/* User menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full focus:outline-none focus:ring-2 focus:ring-zinc-700" data-testid="user-menu-btn">
                    <UserAvatar user={user} accent={accent} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-white">{user?.name || "Player"}</p>
                    <p className="text-xs text-zinc-500">{user?.email || user?.phone}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem
                    className="text-zinc-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => navigate("/profile")}
                  >
                    <Users className="w-4 h-4 mr-2 lg:w-5 lg:h-5" /> Profile & Quiz
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-zinc-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => navigate("/wallet")}
                  >
                    <Video className="w-4 h-4 mr-2 lg:w-5 lg:h-5" /> Analyses & wallet
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-zinc-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => navigate("/card")}
                  >
                    <CreditCard className="w-4 h-4 mr-2 lg:w-5 lg:h-5" /> My Card
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-zinc-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => navigate("/progress")}
                  >
                    <BarChart3 className="w-4 h-4 mr-2 lg:w-5 lg:h-5" /> Progress
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem
                    className="text-zinc-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => navigate("/download")}
                  >
                    <Download className="w-4 h-4 mr-2 lg:w-5 lg:h-5" /> Get the App
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-zinc-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => navigate("/help")}
                  >
                    <span className="w-4 mr-2 text-center">?</span> Help & Support
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => { logout(); navigate("/"); }}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2 lg:w-5 lg:h-5" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button size="sm" onClick={() => navigate("/auth")}
                className="bg-lime-400 text-black hover:bg-lime-500 font-bold rounded-full text-xs sm:text-sm"
                data-testid="nav-login-btn">
                Login
              </Button>
            )}
          </div>
      </NavShell>

      {/* ── Mobile Bottom Navigation Bar ── */}
      {showNav && (
        <div
          className={`md:hidden fixed bottom-0 left-0 right-0 z-50 mobile-bottom-nav ${navPulse ? "nav-pulse" : ""}`}
          data-testid="mobile-bottom-nav"
        >
          <div className="flex items-center justify-around h-[68px] px-1 relative">
            {MOBILE_NAV_PRIMARY.map((item) => {
              const { label, icon: Icon } = item;
              // guests' "Home" is the home page itself, not the second guest landing at /dashboard
              const path = isGuest && item.path === "/dashboard" ? "/" : item.path;
              const isActive = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  className={`relative flex flex-col items-center justify-center gap-1 flex-1 py-1.5 rounded-xl transition-colors min-h-[52px] ${
                    isActive ? accent.active : "text-zinc-300 active:bg-zinc-800/60"
                  }`}
                  data-testid={`mobile-nav-${label.toLowerCase()}`}
                >
                  <Icon className="w-6 h-6 lg:w-7 lg:h-7" strokeWidth={isActive ? 2.2 : 1.8} />
                  {isNewItem(item) && <span className="absolute top-1 left-1/2 ml-2.5 w-2 h-2 rounded-full bg-lime-400 ring-2 ring-zinc-950" aria-label="New" />}
                  <span className={`text-[11px] font-semibold ${isActive ? "" : "text-zinc-400"}`}>{label}</span>
                  {isActive && (
                    <motion.div
                      layoutId="mobile-nav-indicator"
                      className={`absolute -top-px w-8 h-[3px] rounded-b-full ${accent.dot}`}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}

            {/* More button */}
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`relative flex flex-col items-center justify-center gap-1 flex-1 py-1.5 rounded-xl transition-colors min-h-[52px] ${
                isMoreActive || moreOpen ? accent.active : "text-zinc-300 active:bg-zinc-800/60"
              }`}
              data-testid="mobile-nav-more"
            >
              <MoreHorizontal className="w-6 h-6 lg:w-7 lg:h-7" strokeWidth={(isMoreActive || moreOpen) ? 2.2 : 1.8} />
              <span className={`text-[11px] font-semibold ${(isMoreActive || moreOpen) ? "" : "text-zinc-400"}`}>More</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Mobile "More" Slide-up Sheet ── */}
      <AnimatePresence>
        {moreOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMoreOpen(false)}
              className="fixed inset-0 bg-black/60 z-40 md:hidden"
            />

            {/* Sheet */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed bottom-16 left-0 right-0 md:hidden" style={{ zIndex: 45 }}
            >
              <div className="bg-zinc-900 border-t border-lime-400/20 rounded-t-2xl overflow-hidden shadow-2xl">
                {/* Handle bar */}
                <div className="flex justify-center py-2">
                  <div className="w-10 h-1 bg-zinc-700 rounded-full" />
                </div>

                <div className="px-4 pb-4 pt-1">
                  {/* First-visit hint banner */}
                  {navPulse && (
                    <div className="mb-3 -mt-1 px-3 py-2 rounded-lg bg-lime-400/10 border border-lime-400/20 text-[11px] text-lime-300 flex items-center gap-2">
                      <span>💡</span>
                      <span><span className="font-semibold text-lime-200">Tip:</span> more pages live behind the <span className="font-semibold">More</span> button below.</span>
                    </div>
                  )}
                  {/* Active sport indicator */}
                  {profile && (
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <Badge className={`${accent.active} text-xs`}>
                        {getSportEmoji(profile.active_sport)} {getSportLabel(profile.active_sport)}
                      </Badge>
                      {streak > 0 && (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-400/10 rounded-full">
                          <Flame className="w-3 h-3 text-amber-400" />
                          <span className="text-[10px] font-bold text-amber-400">{streak}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* More nav items */}
                  <div className="grid grid-cols-3 gap-2">
                    {MOBILE_NAV_MORE.map(({ path, label, icon: Icon }) => {
                      const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
                      return (
                        <Link
                          key={path}
                          to={path}
                          onClick={() => setMoreOpen(false)}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors min-h-[64px] ${
                            isActive ? accent.active : "text-zinc-400 hover:bg-zinc-800"
                          }`}
                        >
                          <Icon className="w-5 h-5 lg:w-6 lg:h-6" strokeWidth={1.5} />
                          <span className="text-xs font-medium">{label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
